/**
 * Advanced Analytics Event Tracking Utility
 * Tracks customer journey events for 360-degree analytics dashboard
 */

import { API_BASE_URL } from '../config';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get customer ID if logged in
const getCustomerId = () => {
  // Check if customer is logged in (adjust based on your auth system)
  const customerData = localStorage.getItem('customerData');
  if (customerData) {
    try {
      const parsed = JSON.parse(customerData);
      return parsed.id || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Extract UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || null,
    utmMedium: params.get('utm_medium') || null,
    utmCampaign: params.get('utm_campaign') || null,
    utmTerm: params.get('utm_term') || null,
    utmContent: params.get('utm_content') || null,
  };
};

// Get device and browser information
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let device = 'desktop';
  if (/mobile|android|iphone|ipad/i.test(ua)) {
    device = 'mobile';
  } else if (/tablet|ipad/i.test(ua)) {
    device = 'tablet';
  }

  let browser = 'unknown';
  if (ua.includes('Chrome')) browser = 'chrome';
  else if (ua.includes('Firefox')) browser = 'firefox';
  else if (ua.includes('Safari')) browser = 'safari';
  else if (ua.includes('Edge')) browser = 'edge';

  let os = 'unknown';
  if (ua.includes('Windows')) os = 'windows';
  else if (ua.includes('Mac')) os = 'mac';
  else if (ua.includes('Linux')) os = 'linux';
  else if (ua.includes('Android')) os = 'android';
  else if (ua.includes('iOS')) os = 'ios';

  return { device, browser, os };
};

// Send event to backend
const sendEvent = async (eventType, eventData = {}) => {
  try {
    const sessionId = getSessionId();
    const customerId = getCustomerId();
    const utmParams = getUTMParams();
    const deviceInfo = getDeviceInfo();
    const baseUrl = API_BASE_URL;

    const eventPayload = {
      sessionId,
      customerId,
      eventType,
      eventData: JSON.stringify(eventData),
      pageUrl: window.location.href,
      referrer: document.referrer || null,
      ...utmParams,
      ...deviceInfo,
      location: null, // Can be enhanced with geolocation if needed
      timestamp: new Date().toISOString(),
    };

    // Send to backend (fire and forget for performance)
    fetch(`${baseUrl}/api/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventPayload),
    }).catch((error) => {
      console.error('Analytics event tracking error:', error);
      // Silently fail - don't interrupt user experience
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track page view
export const trackPageView = (pageName = null) => {
  sendEvent('page_view', {
    pageName: pageName || window.location.pathname,
    pageTitle: document.title,
  });
};

// Track product view
export const trackProductView = (productId, productName = null, productPrice = null, category = null) => {
  sendEvent('product_view', {
    productId,
    productName,
    productPrice,
    category,
  });
};

// Track add to cart
export const trackAddToCart = (productId, quantity = 1, price = null, cartValue = null) => {
  sendEvent('add_to_cart', {
    productId,
    quantity,
    price,
    cartValue,
  });
};

// Track checkout start
export const trackCheckoutStart = (cartValue = null, itemCount = 0) => {
  sendEvent('checkout_start', {
    cartValue,
    itemCount,
  });
};

// Track payment initiated
export const trackPaymentInitiated = (paymentMethod = null, amount = null) => {
  sendEvent('payment_initiated', {
    paymentMethod,
    amount,
  });
};

// Track purchase completion
export const trackPurchase = (orderId, orderNumber, total = null, itemCount = 0, products = []) => {
  sendEvent('purchase', {
    orderId,
    orderNumber,
    total,
    itemCount,
    products: products.map(p => ({ id: p.id, name: p.name, quantity: p.quantity })),
  });
};

// Track cart abandonment
export const trackCartAbandon = (cartValue = null, itemCount = 0, timeInCart = null) => {
  sendEvent('cart_abandon', {
    cartValue,
    itemCount,
    timeInCart,
  });
};

// Track funnel stage
export const trackFunnelStage = async (stage, previousStage = null, timeAtStage = null, dropOffReason = null, productIds = null, cartValue = null) => {
  try {
    const sessionId = getSessionId();
    const customerId = getCustomerId();
    const baseUrl = API_BASE_URL;

    const funnelPayload = {
      sessionId,
      customerId,
      funnelStage: stage,
      previousStage,
      timeAtStage,
      dropOffReason,
      productIds: productIds ? JSON.stringify(productIds) : null,
      cartValue,
      timestamp: new Date().toISOString(),
    };

    fetch(`${baseUrl}/api/analytics/funnel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(funnelPayload),
    }).catch((error) => {
      console.error('Funnel tracking error:', error);
    });
  } catch (error) {
    console.error('Funnel tracking error:', error);
  }
};

// Initialize analytics tracking
export const initAnalytics = () => {
  // Track initial page view
  trackPageView();

  // Track page visibility changes (when user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      trackPageView();
    }
  });

  // Track before page unload (for cart abandonment)
  window.addEventListener('beforeunload', () => {
    // Check if cart has items but no purchase was made
    const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
    if (cartItems.length > 0) {
      const cartValue = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      trackCartAbandon(cartValue, cartItems.length);
    }
  });
};

export default {
  trackPageView,
  trackProductView,
  trackAddToCart,
  trackCheckoutStart,
  trackPaymentInitiated,
  trackPurchase,
  trackCartAbandon,
  trackFunnelStage,
  initAnalytics,
};







